import * as React from 'react';
import { Title } from '@patternfly/react-core';
import type { Payload } from '../../../../shared/riskregister/types';

const DEFINITIONS = [
  {
    title: 'Due',
    body: 'Overdue means an open item has a due date before the as-of date. At risk means it is due within the configured warning window.',
  },
  {
    title: 'Idle',
    body: 'Idle is days since the last recorded status or relevant item movement. An asterisk means the starting date was estimated.',
  },
  {
    title: 'Risk category',
    body: 'The RiskRegister category describes the kind of risk. A missing category is shown explicitly and is not inferred from summary text.',
  },
  {
    title: 'Risk score',
    body: 'The measured score combines the registered risk dimensions. Missing scores remain unscored rather than being assigned a default.',
  },
  {
    title: 'Completed',
    body: 'Completed items are retained to show paydown. Completion is reported separately from open work and is not silently removed from history.',
  },
  {
    title: 'Trend',
    body: 'Trend compares items opened against items resolved in the selected period. The cumulative line retains its full-history starting value.',
  },
  {
    title: 'Stage and limbo',
    body: 'Not started, in progress, and completed are the workflow stages. Limbo is open, not started, unassigned work idle at least the stale threshold.',
  },
  {
    title: 'Team',
    body: 'Team is resolved from the accountable owner’s active Cortex team where available. Retired teams are not preferred over active teams.',
  },
  {
    title: 'Manager',
    body: 'Manager is mapped from Cortex and may come through a parent team. The page never invents a manager when the mapping is unavailable.',
  },
  {
    title: 'Due moves',
    body: 'Due moves show all historical changes to a due date, including slips, pull-ins, dates set, and dates cleared.',
  },
  {
    title: 'Added and tiers',
    body: 'Added is when the RiskRegister label was applied, not ticket creation. Tiers are Critical (13–15), High (10–12), Moderate (5–9), or Not scored.',
  },
  {
    title: 'Platform area confidence',
    body: 'Platform area confidence records whether ownership is confirmed, inferred, ambiguous, or unmapped so grouped totals do not hide uncertainty.',
  },
];

export const Definitions: React.FunctionComponent<{ payload: Payload }> = ({ payload }) => {
  const caveats = payload.meta.caveats;
  const entries: Array<[string, number, string]> = [
    ['Unmapped projects', caveats.unmapped_projects, 'row(s) belong to projects without a platform-area mapping.'],
    [
      'Missing risk scores',
      caveats.missing_risk_scores,
      'row(s) cannot be assessed until Jira scoring data is available.',
    ],
    ['Missing risk categories', caveats.missing_risk_categories, 'row(s) have no RiskRegister category in Jira.'],
    ['Missing managers', caveats.missing_managers, 'row(s) have no manager mapping in Cortex.'],
    [
      'Unreadable change records',
      caveats.unreadable_changes,
      'change record(s) could not be read for delivery evidence.',
    ],
    [
      'Conservation drift',
      caveats.conservation_drift,
      'item(s) differ between the labeled population and rendered rows.',
    ],
  ];

  return (
    <div className="risk-register__panel-stack">
      <div className="risk-register__definition-grid">
        {DEFINITIONS.map((definition) => (
          <div key={definition.title} className="risk-register__definition">
            <Title headingLevel="h3" size="md">
              {definition.title}
            </Title>
            <p className="risk-register__definition-copy">{definition.body}</p>
          </div>
        ))}
      </div>
      <div aria-label="Data caveats" className="risk-register__callout risk-register__callout--warning">
        <Title headingLevel="h3" size="md">
          Data caveats
        </Title>
        <ul className="risk-register__callout-copy">
          {entries.map(([label, count, detail]) => (
            <li key={label}>
              <strong>{count}</strong> {label.toLowerCase()}: {detail}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
